/**
 * components/wishlist/item-detail-sheet.tsx
 *
 * Bottom sheet showing full item details with full attribution, note, link,
 * and done/nudge/edit/delete action buttons.
 */
import type { WishlistItem } from '@just-us/shared'
import Attribution from './attribution'
import LinkPreview from './link-preview'
import VibeChip from './vibe-chip'

interface ItemDetailSheetProps {
  item: WishlistItem
  partnerNickname: string
  isNudging: boolean
  isDeletingItem: boolean
  onNudge: (item: WishlistItem) => void
  onEdit: (item: WishlistItem) => void
  onMarkDone: (item: WishlistItem) => void
  onDelete: (item: WishlistItem) => void
  onClose: () => void
}

const isWithin24h = (ts: number) => Date.now() - ts * 1000 < 86_400_000
const hoursUntilNudgeReset = (nudgedAt: number): number =>
  Math.ceil((nudgedAt * 1000 + 86_400_000 - Date.now()) / 3_600_000)

export default function ItemDetailSheet({
  item,
  partnerNickname,
  isNudging,
  isDeletingItem,
  onNudge,
  onEdit,
  onMarkDone,
  onDelete,
  onClose,
}: ItemDetailSheetProps) {
  const nudgeBlocked = !!(item.nudgedAt && isWithin24h(item.nudgedAt))

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-[60] animate-fade-in"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 max-w-[430px] mx-auto z-[70] bg-white rounded-t-[24px] shadow-[0_-4px_40px_rgba(0,0,0,0.12)] animate-slide-up pb-[env(safe-area-inset-bottom)]">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-[36px] h-[4px] rounded-full bg-[#F4C0D1]" />
        </div>

        <div className="px-5 pt-2 pb-6">
          {/* Vibe chip */}
          <div className="mb-3">
            <VibeChip vibe={item.vibe} />
          </div>

          {/* Title */}
          <h2
            className={`font-['Playfair_Display'] text-[20px] font-medium text-[#2C1810] mb-1 ${
              item.doneAt ? 'line-through text-[#B89AA2]' : ''
            }`}
          >
            {item.title}
          </h2>

          {/* Attribution with timestamp */}
          <Attribution
            isAddedByMe={item.isAddedByMe}
            addedByNickname={item.addedByNickname}
            addedByAvatar={item.addedByAvatar}
            createdAt={item.createdAt}
            variant="full"
          />

          {/* Note */}
          {item.note && (
            <div className="mt-3 bg-[#FAF8F4] rounded-[12px] px-4 py-3 border border-[#F9E0E8]">
              <p className="font-['DM_Sans'] text-[14px] text-[#7A5A62] leading-relaxed">
                {item.note}
              </p>
            </div>
          )}

          {/* Link */}
          {item.linkUrl && (
            <div className="mt-3">
              <LinkPreview
                linkUrl={item.linkUrl}
                linkDomain={item.linkDomain}
                linkFavicon={item.linkFavicon}
                linkTitle={item.linkTitle}
              />
            </div>
          )}

          {/* Done attribution */}
          {item.doneAt && (
            <div className="mt-3 flex items-center gap-2 py-3 border-b border-[#F9E0E8]">
              <span className="text-[20px]">✅</span>
              <p className="font-['DM_Sans'] text-[13px] text-[#2C1810]">
                Done by {item.isDoneByMe ? 'you' : item.doneByNickname}
                {' · '}
                {new Date(item.doneAt * 1000).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-5 flex flex-col gap-2">
            {!item.doneAt && !item.isAddedByMe && (
              <button
                type="button"
                disabled={nudgeBlocked || isNudging}
                onClick={() => { onNudge(item); onClose() }}
                className="
                  flex items-center justify-center gap-2 w-full h-[48px] rounded-[14px]
                  bg-[#FBEAF0] border border-[#F4C0D1]
                  font-['DM_Sans'] text-[14px] font-medium text-[#993556]
                  active:bg-[#F9E0E8] transition-colors duration-100 touch-manipulation
                  disabled:opacity-50
                "
              >
                <span>👀</span>
                {nudgeBlocked && item.nudgedAt
                  ? `Nudge again in ${hoursUntilNudgeReset(item.nudgedAt)}h`
                  : `Nudge ${partnerNickname}`}
              </button>
            )}

            {!item.doneAt && item.isAddedByMe && (
              <button
                type="button"
                onClick={() => { onEdit(item); onClose() }}
                className="
                  flex items-center justify-center gap-2 w-full h-[48px] rounded-[14px]
                  bg-[#FBEAF0] border border-[#F4C0D1]
                  font-['DM_Sans'] text-[14px] font-medium text-[#993556]
                  active:bg-[#F9E0E8] transition-colors duration-100 touch-manipulation
                "
              >
                <span>✏️</span>
                Edit item
              </button>
            )}

            {!item.doneAt && (
              <button
                type="button"
                onClick={() => { onMarkDone(item); onClose() }}
                className="
                  flex items-center justify-center gap-2 w-full h-[48px] rounded-[14px]
                  bg-[#E1F5EE] border border-[#9FE1CB]
                  font-['DM_Sans'] text-[14px] font-medium text-[#085041]
                  active:bg-[#C8EFE4] transition-colors duration-100 touch-manipulation
                "
              >
                <span>✅</span>
                Done together
              </button>
            )}

            {item.isAddedByMe && (
              <button
                type="button"
                disabled={isDeletingItem}
                onClick={() => { onDelete(item); onClose() }}
                className="
                  flex items-center justify-center gap-2 w-full h-[48px] rounded-[14px]
                  bg-white border border-[#F9E0E8]
                  font-['DM_Sans'] text-[14px] font-medium text-[#C0392B]
                  active:bg-[#FCEBEB] transition-colors duration-100 touch-manipulation
                  disabled:opacity-50
                "
              >
                <span>🗑️</span>
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
